import { type DragEvent as ReactDragEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import IcDocument from '@/assets/icons/Document.svg?react';
import IcFileStack from '@/assets/icons/FileStack.svg?react';
import IcPaperPlane from '@/assets/icons/PaperPlane.svg?react';
import useFileDrop from '@/hooks/useFileDrop';
import { type AcceptedFileType } from '@/utils/acceptedFileAttributes';
import { isSupportedFile } from '@/utils/dragSupport.util';
import Card from './Card';
import styles from './StartSomethingNew.module.scss';

// NewSigning's DefineDocument reads this entry on mount and rehydrates the
// dropped file into its upload state.
const HANDOVER_KEY = 'tmp-pdf-handover';
const ACCEPTED_FILE_TYPES: AcceptedFileType[] = ['PDF', 'DOCX'];

const StartSomethingNew = () => {
  const { t } = useTranslation(['common']);
  const navigate = useNavigate();

  // Track drag state for the card visuals; the drop itself is captured by the
  // PDF card below, which hands the file off to the new-signing flow.
  const { dragState } = useFileDrop({ fileType: ACCEPTED_FILE_TYPES });

  const handleCardDragOver = (e: ReactDragEvent<HTMLAnchorElement>) => {
    // Only allow the drop when the dragged payload is something we accept.
    if (dragState === 'supported') {
      e.preventDefault();
    }
  };

  const handleCardDrop = (e: ReactDragEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (!file) {
      return;
    }
    if (!isSupportedFile(file, ACCEPTED_FILE_TYPES)) {
      toast.error(t('common:dashboard.startNew.pdf.unsupportedToast'));
      return;
    }

    const objectUrl = URL.createObjectURL(file);
    sessionStorage.setItem(
      HANDOVER_KEY,
      JSON.stringify({ file: objectUrl, name: file.name, type: file.type })
    );
    void navigate('/signings/new');
  };

  const pdfCard = (() => {
    if (dragState === 'supported') {
      return {
        title: t('common:dashboard.startNew.pdf.titleDropping'),
        subtitle: t('common:dashboard.startNew.pdf.subtitleDropping'),
        state: 'active' as const,
      };
    }
    if (dragState === 'unsupported') {
      return {
        title: t('common:dashboard.startNew.pdf.titleUnsupported'),
        subtitle: t('common:dashboard.startNew.pdf.subtitleUnsupported'),
        state: 'rejected' as const,
      };
    }
    return {
      title: t('common:dashboard.startNew.pdf.title'),
      subtitle: t('common:dashboard.startNew.pdf.subtitle'),
      state: 'idle' as const,
    };
  })();

  return (
    <section className={styles.section}>
      <h2 className={styles.heading}>{t('common:dashboard.startNew.title')}</h2>
      <div className={styles.grid}>
        <Card
          to="/signings/new"
          title={pdfCard.title}
          subtitle={pdfCard.subtitle}
          icon={<IcDocument />}
          state={pdfCard.state}
          onDragOver={handleCardDragOver}
          onDrop={handleCardDrop}
        />
        <Card
          to="/templates"
          title={t('common:dashboard.startNew.template.title')}
          subtitle={t('common:dashboard.startNew.template.subtitle')}
          icon={<IcFileStack />}
        />
        <Card
          to="/signings/new"
          title={t('common:dashboard.startNew.request.title')}
          subtitle={t('common:dashboard.startNew.request.subtitle')}
          icon={<IcPaperPlane />}
        />
      </div>
    </section>
  );
};

export default StartSomethingNew;
